<template>
  <v-tour :name="tourName" :steps="steps" :options="OPTIONS" >
    <template slot-scope="tour">
        <onboarding-step
          :tour="tour"
          :tour-name="tourName"
          :standalone="standalone"
          class="mt-2"
          slot="content"
          @dismiss="$emit('dismiss')" />
    </template>
  </v-tour>
</template>
<script lang="ts" setup>
import OnboardingStep from '@/modules/onboarding/OnboardingStep.vue';
import {TourName} from '@/modules/onboarding/tour/models/TourOptions';
import {TourStep} from '@/modules/onboarding/tour/models/TourStep';
import {onKeyStroke} from '@vueuse/core';
import {computed, getCurrentInstance, onMounted} from 'vue';
import {Tour} from '@/modules/onboarding/tour/Tour';

const props = withDefaults(defineProps<{
  tourName: TourName,
  steps: TourStep[],
  // Standalone single-step bubble: self-starts and is driven by `dismiss` (no Tour gating / START
  // modal). Used outside the tutorial flow, e.g. platform notifications.
  standalone?: boolean,
}>(), {
  standalone: false,
});

defineEmits(['dismiss']);

const OPTIONS = {
  // disable the default keyboard navigation
  useKeyboardNavigation: false,
};

const instance = getCurrentInstance();
const onboarding = computed<Tour>(() => instance!.proxy.$onboarding.tours[props.tourName]);

onKeyStroke('ArrowLeft', () => {
  if (!props.standalone) {
    onboarding.value?.previousStep();
  }
});

onKeyStroke('ArrowRight', () => {
  if (!props.standalone) {
    onboarding.value?.nextStep();
  }
});

onMounted(() => {
  // In standalone mode there is no external driver (no START modal / Tour.goToStep), so start the
  // single-step bubble ourselves. The child <v-tour> has registered into $tours by now.
  if (props.standalone) {
    instance!.proxy.$tours?.[props.tourName]?.start();
  }
});
</script>
<style lang="scss">
:root {
  --tour-colour: #0A73EB33;
  --tour-border-colour: #0A73EB;
}
.v-tour {
  &__target--highlighted {
    z-index: 200;
    position: relative;

    &:after {
      border-radius: 4px;
      min-height: 100%;
      min-width: 100%;
      pointer-events: none;
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      animation-name: blink;
      animation-duration: 1s;
      animation-iteration-count: 3;
      border: solid 2px var(--tour-border-colour);
      background-color: var(--tour-colour);
    }
  }
  .v-step {
    min-width: 200px;
    background: transparent;
    padding: 0;
    box-shadow: none;

    &__arrow {
      background: var(--token-primary-base);
      &:before {
        border-radius: 50%;
      }
    }
  }

  @keyframes blink {
    0% {
      background-color: var(--tour-colour);
    }
    50% {
      background-color: transparent;
    }
    100% {
      background-color: var(--tour-colour);
    }
  }
}

</style>
